/* Client-side latency + error metrics, recorded off the shared OTEL `meter`.
 *
 * Safe no-op in two cases, so importing this never throws:
 *   - telemetry unconfigured (empty VITE_TELEMETRY_BASE_URL ⇒ the meter's
 *     exporter posts nowhere), and
 *   - the jest telemetry mock (a bare `meter` with no instrument factories).
 * The second is why we feature-detect `createHistogram` rather than assume it.
 *
 * Labels are deliberately low-cardinality — `origin` (the bounded operation
 * discriminator from useQuery) and a numeric HTTP `status` — so Prometheus
 * series don't explode. Never label by URL, workspace, or id. */

import { meter } from "./telemetry";

/** Metric labels. Kept to a small, bounded set on purpose (see file header). */
type MetricAttrs = { origin: string; status: number };

const hasFactories =
  typeof (meter as { createHistogram?: unknown }).createHistogram ===
  "function";

const durationHistogram = hasFactories
  ? meter.createHistogram("http_client_duration_ms", {
      description: "Client-observed API request duration in milliseconds.",
      unit: "ms",
    })
  : undefined;

const errorCounter = hasFactories
  ? meter.createCounter("http_client_errors_total", {
      description: "Client-observed API request failures.",
    })
  : undefined;

/** Record a settled API request's duration, labelled by origin + HTTP status. */
export function recordApiDuration(
  origin: string,
  status: number,
  durationMs: number,
): void {
  durationHistogram?.record(durationMs, {
    origin,
    status,
  } satisfies MetricAttrs);
}

/** Record an API request failure, labelled by origin + HTTP status. */
export function recordApiError(origin: string, status: number): void {
  errorCounter?.add(1, { origin, status } satisfies MetricAttrs);
}
